import { FunctionComponent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducer';
import { Tooltip } from '../../tailwindcomponents';
import { SvgIcon } from '../icons/SvgIcon';
import { SettingsPanel } from '../../design/shell/SettingsPanel';
import { useDsTheme } from '../../design/ThemeProvider';
import ProfileImg from '../Images/ProfileImg';
import railMark from '../../assets/smallIcelandairLogo.png';
import { FEATURE_FLAGS } from '../../config/featureFlags';

type SidebarFooterProps = {
    collapsed: boolean,
}

/**
 * Pinned to the bottom of the sidebar in both states: the light/dark toggle,
 * settings, the signed-in user, and the Icelandair mark.
 *
 * Always a column of four rows. Switching `flex-direction` between collapsed
 * and expanded would snap in the same frame the width animates - the labels
 * fade out instead and the rows keep their geometry.
 */
const SidebarFooter: FunctionComponent<SidebarFooterProps> = ({ collapsed }) => {
    const [settingsOpen, setSettingsOpen] = useState(false);
    const me = useSelector((state: RootState) => state.user.me);
    // The app-level ThemeProvider in src/index.tsx sets applyToRoot, so this
    // writes data-ds-mode onto #root and restyles the whole app - not just the
    // sidebar. The mode already rides the persisted prefs object, so it sticks.
    const { mode, toggleMode } = useDsTheme();
    const isDark = mode === 'dark';

    return (
        <div className="ds-sidebar-footer flex flex-col gap-0.5">
            {FEATURE_FLAGS.darkModeToggle && (
                <Tooltip title={isDark ? 'Light mode' : 'Dark mode'} placement="right-start" type="primary" disabled={!collapsed}>
                    <button type="button" className="ds-nav-item w-full" onClick={toggleMode}>
                        <span className="ds-nav-icon">
                            <SvgIcon name={isDark ? 'lightMode' : 'nightlightRound'} size={18} />
                        </span>
                        <span className="ds-nav-label">{isDark ? 'Light mode' : 'Dark mode'}</span>
                    </button>
                </Tooltip>
            )}

            {FEATURE_FLAGS.displaySettings && (
                <Tooltip title="Display settings" placement="right-start" type="primary" disabled={!collapsed}>
                    <button type="button" className="ds-nav-item w-full" onClick={() => setSettingsOpen(true)}>
                        <span className="ds-nav-icon"><SvgIcon name="settings" size={18} /></span>
                        <span className="ds-nav-label">Settings</span>
                    </button>
                </Tooltip>
            )}

            <Tooltip title={me.display_name ?? 'My Profile'} placement="right-start" type="primary" disabled={!collapsed}>
                <Link to="/my-profile" className="ds-nav-item">
                    <span className="ds-nav-icon w-6">
                        {/* xs (24px) matches .ds-avatar, so the row height is identical
                         * collapsed and expanded and nothing reflows mid-transition. */}
                        <ProfileImg imgSize="xs" userName={me.user_name?.split('@')[0]} />
                    </span>
                    <span className="ds-nav-label min-w-0">
                        <span className="block truncate">{me.display_name}</span>
                        <span className="block truncate text-[length:var(--ds-fs-xs)] text-[color:var(--ds-sidebar-muted)]">
                            {me.job_title}
                        </span>
                    </span>
                </Link>
            </Tooltip>

            <Tooltip title="Hangar Overview" placement="right-start" type="primary" disabled={!collapsed}>
                <Link to="/hangar-overview" className="ds-nav-item">
                    <span className="ds-nav-icon">
                        {/* The square mark, not the white wordmark: it is opaque navy so
                         * it needs no per-mode filter and it fits the 56px rail. */}
                        <span className="ds-rail-brand"><img src={railMark} alt="" className="h-full w-full object-contain" /></span>
                    </span>
                    <span className="ds-nav-label">Icelandair</span>
                </Link>
            </Tooltip>

            {FEATURE_FLAGS.displaySettings && (
                <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} />
            )}
        </div>
    );
};

export default SidebarFooter;
